import styled from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { reducedMotion } from '../../atome/motion/motion.styled';

export const CollectionSection = styled.section`
  position: relative;
  padding: 4rem 0 4.5rem;
  background:
    radial-gradient(90% 60% at 100% 0%, ${color.sandLight} 0%, transparent 60%),
    ${color.cream};
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;

  h2 {
    font-family: ${font.display};
    font-size: clamp(2.1rem, 4.2vw, 3.2rem);
    font-weight: 600;
    line-height: 1.08;
    color: ${color.ink};
    margin: 0.7rem 0 0.5rem;
  }

  p {
    font-family: ${font.body};
    color: ${color.inkSoft};
    max-width: 46ch;
    margin: 0;
    line-height: 1.7;
  }
`;

export const CountBadge = styled.span`
  font-family: ${font.body};
  font-size: 0.78rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${color.forest};
  padding: 0.5rem 1rem;
  border: 1px solid ${color.line};
  border-radius: ${radius.pill};
  white-space: nowrap;
`;

export const Filters = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 2.4rem;
`;

export const FilterChip = styled.button<{ $active: boolean }>`
  padding: 0.55rem 1.15rem;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.83rem;
  letter-spacing: 0.06em;
  text-transform: capitalize;
  color: ${({ $active }) => ($active ? '#fff' : color.inkSoft)};
  background: ${({ $active }) => ($active ? color.forest : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? color.forest : color.line)};
  transition:
    background ${duration.base} ${ease.out},
    color ${duration.base} ${ease.out},
    transform ${duration.fast} ${ease.spring};

  &:hover {
    transform: translateY(-2px);
    border-color: ${color.forest};
    color: ${({ $active }) => ($active ? '#fff' : color.forest)};
  }

  ${reducedMotion};
`;

/**
 * Editorial grid: the first card claims two columns on desktop so the
 * catalogue reads like a magazine spread rather than a spreadsheet.
 */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(255px, 1fr));
  gap: 1.6rem;

  @media (min-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

/**
 * Grid cell. Owns the span so the animated `Reveal` wrapper inside can stay
 * a plain block without fighting the grid.
 */
export const GridItem = styled.div<{ $featured?: boolean }>`
  display: flex;

  /* the Reveal wrapper */
  & > div {
    display: flex;
    width: 100%;
  }

  @media (min-width: 1000px) {
    ${({ $featured }) => $featured && 'grid-column: span 2; grid-row: span 2;'}
  }
`;

export const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: ${radius.lg};
  overflow: hidden;
  background: ${color.paper};
  box-shadow: ${shadow.soft};
  /* The card itself is not clickable — only its "Add to bag" button is. */
  cursor: default;
  transition:
    transform ${duration.base} ${ease.out},
    box-shadow ${duration.base} ${ease.out};

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${shadow.lift};
  }

  ${reducedMotion};
`;

export const Media = styled.div<{ $featured?: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $featured }) => ($featured ? '16 / 12' : '4 / 4.4')};
  overflow: hidden;
  background: linear-gradient(150deg, ${color.sandLight}, ${color.creamDeep});

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 1.1s ${ease.out};
  }

  ${Card}:hover & img {
    transform: scale(1.07);
  }

  /* darkening veil that carries the quick-action button */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(43, 42, 39, 0) 45%, rgba(43, 42, 39, 0.42) 100%);
    opacity: 0;
    transition: opacity ${duration.base} ${ease.out};
  }

  ${Card}:hover &::after {
    opacity: 1;
  }
`;

export const QuickAdd = styled.button`
  position: absolute;
  left: 50%;
  bottom: 18px;
  transform: translate(-50%, 22px);
  z-index: 2;
  padding: 0.7rem 1.4rem;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.84rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: ${color.forestDeep};
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 26px rgba(43, 42, 39, 0.22);
  opacity: 0;
  transition:
    opacity ${duration.base} ${ease.out},
    transform ${duration.base} ${ease.spring},
    background ${duration.fast} ${ease.out};

  ${Card}:hover & {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  &:hover {
    background: ${color.forest};
    color: #fff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    background: rgba(255, 255, 255, 0.8);
    color: ${color.inkFaint};
  }

  @media (hover: none) {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  ${reducedMotion};
`;

export const Ribbon = styled.span<{ $tone?: 'sale' | 'muted' }>`
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 2;
  padding: 0.36rem 0.8rem;
  border-radius: ${radius.pill};
  font-family: ${font.body};
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $tone }) => ($tone === 'muted' ? color.ink : '#fff')};
  background: ${({ $tone }) => ($tone === 'muted' ? 'rgba(255,255,255,0.92)' : color.forest)};
  box-shadow: 0 6px 16px rgba(43, 42, 39, 0.16);
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 1.25rem 1.3rem 1.4rem;
  flex: 1;
`;

export const Category = styled.span`
  font-family: ${font.body};
  font-size: 0.66rem;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: ${color.inkFaint};
`;

export const Name = styled.h3`
  font-family: ${font.display};
  font-size: 1.45rem;
  font-weight: 600;
  line-height: 1.2;
  color: ${color.ink};
  margin: 0.1rem 0;
  transition: color ${duration.fast} ${ease.out};

  ${Card}:hover & {
    color: ${color.forest};
  }
`;

export const Blurb = styled.p`
  font-family: ${font.body};
  font-size: 0.88rem;
  line-height: 1.6;
  color: ${color.inkSoft};
  margin: 0 0 0.4rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  margin-top: auto;
  padding-top: 0.6rem;

  strong {
    font-family: ${font.display};
    font-size: 1.35rem;
    font-weight: 600;
    color: ${color.forest};
  }

  s {
    font-family: ${font.body};
    font-size: 0.9rem;
    color: ${color.inkFaint};
  }

  em {
    margin-left: auto;
    font-family: ${font.body};
    font-style: normal;
    font-size: 0.74rem;
    color: ${color.inkFaint};
  }
`;

export const EmptyState = styled.div`
  padding: 4rem 1.5rem;
  text-align: center;
  border: 1px dashed ${color.line};
  border-radius: ${radius.lg};
  background: rgba(255, 255, 255, 0.6);

  h3 {
    font-family: ${font.display};
    font-size: 1.8rem;
    color: ${color.ink};
    margin: 0 0 0.5rem;
  }

  p {
    font-family: ${font.body};
    color: ${color.inkSoft};
    margin: 0;
  }
`;
